import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  useToast,
  Switch,
  Box,
  Flex,
  HStack,
  Text,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState, useEffect } from 'react';
import { RiShareLine, RiBuilding2Line } from 'react-icons/ri';
import {
  CustomAgent,
  useShareAgentWithOrganizationMutation,
  useUnshareAgentWithOrganizationMutation,
  useGetMyOrganizationsWithRolesQuery,
} from '../../generated/graphql';

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

interface ShareAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: CustomAgent;
  onSuccess: () => void;
}

// Agents are org-scoped: they live in their home organization and are visible to its
// members. This modal grants additional organizations access (cross-org sharing).
export const ShareAgentModal = ({
  isOpen,
  onClose,
  agent,
  onSuccess,
}: ShareAgentModalProps) => {
  const toast = useToast();

  const [orgShareIds, setOrgShareIds] = useState<Set<string>>(
    new Set((agent.orgShares ?? []).map((s) => s.organizationId))
  );
  const [orgActionId, setOrgActionId] = useState<string | null>(null);
  const [shareWithOrg] = useShareAgentWithOrganizationMutation();
  const [unshareWithOrg] = useUnshareAgentWithOrganizationMutation();
  const { data: orgsData } = useGetMyOrganizationsWithRolesQuery({ skip: !isOpen });
  const myOrgs = orgsData?.getMyOrganizationsWithRoles ?? [];

  // Re-sync local state when the agent prop changes (e.g. modal reopened)
  useEffect(() => {
    setOrgShareIds(new Set((agent.orgShares ?? []).map((s) => s.organizationId)));
  }, [agent.id, agent.orgShares]);

  const handleToggleOrg = async (organizationId: string, currentlyShared: boolean) => {
    setOrgActionId(organizationId);
    try {
      if (currentlyShared) {
        const res = await unshareWithOrg({ variables: { agentId: agent.id, organizationId } });
        setOrgShareIds(new Set((res.data?.unshareAgentWithOrganization.orgShares ?? []).map((s) => s.organizationId)));
      } else {
        const res = await shareWithOrg({ variables: { agentId: agent.id, organizationId } });
        setOrgShareIds(new Set((res.data?.shareAgentWithOrganization.orgShares ?? []).map((s) => s.organizationId)));
      }
      onSuccess();
    } catch (error) {
      toast({
        title: 'Error updating organization access',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setOrgActionId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent bg="transparent" boxShadow="none" mx={4} overflow="visible" maxH="85vh">
        {/* Outer glow effect */}
        <Box
          position="absolute"
          top="-2px"
          left="-2px"
          right="-2px"
          bottom="-2px"
          borderRadius="28px"
          bg="linear-gradient(135deg, rgba(0,71,125,0.3) 0%, rgba(72,104,132,0.2) 50%, rgba(0,71,125,0.3) 100%)"
          backgroundSize="200% 200%"
          animation={`${gradientShift} 5s ease infinite`}
          filter="blur(8px)"
          opacity={0.6}
          zIndex={-1}
        />

        <Box
          bg="white"
          borderRadius="24px"
          overflow="hidden"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 20px 25px -5px rgba(0, 0, 0, 0.1)"
          display="flex"
          flexDirection="column"
          maxH="85vh"
        >
          {/* Header */}
          <Box position="relative" overflow="hidden" flexShrink={0}>
            <Box
              h="4px"
              bgGradient="linear(to-r, #00477D, #486884, #00477D)"
              backgroundSize="200% 100%"
              animation={`${gradientShift} 3s ease infinite`}
            />
            <Flex px={6} pt={6} pb={4} align="center" gap={4}>
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
                align="center"
                justify="center"
                boxShadow="0 4px 14px rgba(0,71,125,0.3)"
              >
                <RiShareLine size={28} color="white" />
              </Flex>
              <Box flex={1}>
                <Text fontSize="20px" fontWeight={700} color="#1a1a2e" letterSpacing="-0.02em">
                  Share Agent
                </Text>
                <Text fontSize="14px" color="#64748b" mt={0.5}>
                  Grant another organization access to this agent
                </Text>
              </Box>
              <ModalCloseButton
                position="relative"
                top={0}
                right={0}
                w="36px"
                h="36px"
                borderRadius="12px"
                bg="#f1f5f9"
                color="#64748b"
                _hover={{ bg: '#e2e8f0', color: '#1a1a2e' }}
                transition="all 0.2s"
              />
            </Flex>
          </Box>

          {/* Body */}
          <ModalBody px={6} py={5} overflowY="auto" flex={1}>
            <Stack spacing={5}>
              <Box bg="#f8fafc" borderRadius="16px" p={5} border="1px solid" borderColor="#e2e8f0">
                <Flex align="center" gap={3} mb={4}>
                  <Flex w="32px" h="32px" borderRadius="10px" bg="rgba(0,71,125,0.1)" align="center" justify="center">
                    <RiBuilding2Line size={16} color="#00477D" />
                  </Flex>
                  <Text fontSize="15px" fontWeight={600} color="#1a1a2e">
                    Organizations
                  </Text>
                </Flex>
                <Text fontSize="13px" color="#94a3b8" mb={3}>
                  Members of an enabled organization can see and use this agent.
                </Text>

                {myOrgs.length === 0 ? (
                  <Text fontSize="13px" color="#94a3b8">
                    You don't belong to any organizations.
                  </Text>
                ) : (
                  <Stack spacing={2}>
                    {myOrgs.map((org) => {
                      const shared = orgShareIds.has(org.id);
                      const isHome = org.id === agent.organizationId;
                      return (
                        <Flex
                          key={org.id}
                          p={3}
                          bg="white"
                          borderRadius="12px"
                          border="1px solid"
                          borderColor="#e2e8f0"
                          justify="space-between"
                          align="center"
                        >
                          <HStack spacing={3}>
                            <RiBuilding2Line size={16} color="#64748b" />
                            <Text fontSize="14px" fontWeight={600} color="#1a1a2e">
                              {org.name}
                            </Text>
                            {isHome && (
                              <Box px={2} py={0.5} borderRadius="6px" bg="rgba(0,71,125,0.1)">
                                <Text fontSize="11px" fontWeight={600} color="#00477D">
                                  Home
                                </Text>
                              </Box>
                            )}
                          </HStack>
                          {/* The home org always has access; only cross-org shares toggle. */}
                          <Switch
                            isChecked={isHome || shared}
                            isDisabled={isHome || orgActionId === org.id}
                            onChange={() => handleToggleOrg(org.id, shared)}
                            colorScheme="blue"
                          />
                        </Flex>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </ModalBody>

          {/* Footer */}
          <Flex
            px={6}
            py={5}
            bg="#f8fafc"
            borderTop="1px solid"
            borderColor="#e2e8f0"
            justify="flex-end"
            flexShrink={0}
          >
            <Box
              as="button"
              px={5}
              py={2.5}
              borderRadius="12px"
              fontSize="14px"
              fontWeight={600}
              color="#64748b"
              bg="white"
              border="1px solid"
              borderColor="#e2e8f0"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={onClose}
            >
              Close
            </Box>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};
